import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Truck, ShieldCheck, Bitcoin } from "lucide-react";
import { MaskLine } from "./Reveal";
import { scrollToId } from "../lib/company";
import { useT } from "../lib/i18n";

const ICONS = [Truck, ShieldCheck, Bitcoin];

const HeroImage = () => {
  const t = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(18% 12% 18% 12% round 28px)", opacity: 0.4 }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)", opacity: 1 }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-[28px] bg-[var(--ink)] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.45)]"
        data-testid="hero-image-frame"
      >
        <motion.img src="/img/hero.jpg" alt="Showroom YOUCARS" style={{ y, scale }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,transparent_30%,rgba(0,0,0,0.55))]" />
        <div className="absolute left-5 bottom-5 md:left-8 md:bottom-8 text-white">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/80">{t.hero.caption}</p>
          <p className="font-display text-lg md:text-2xl font-semibold">Corso Bergamo 108/D</p>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute -top-6 -right-2 md:-top-10 md:-right-8 w-24 h-24 md:w-32 md:h-32"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_22s_linear_infinite]">
          <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <circle cx="50" cy="50" r="49" fill="#D7261E" />
          <text fill="#fff" fontSize="9.5" fontFamily="JetBrains Mono" letterSpacing="2.2">
            <textPath href="#c" textLength="235" lengthAdjust="spacingAndGlyphs">{t.hero.badge}</textPath>
          </text>
        </svg>
        <span className="absolute inset-0 grid place-items-center font-display font-bold text-white text-xl md:text-2xl">EU</span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const t = useT();
  return (
  <section className="relative pt-[110px] md:pt-[140px] pb-16 md:pb-24 overflow-hidden grain" data-testid="hero-section">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
      <div className="lg:col-span-6 relative z-10">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--red)] mb-6">{t.hero.eyebrow}</motion.p>
        <h1 className="font-display font-semibold tracking-[-0.04em] leading-[0.92] text-[clamp(3rem,8.4vw,7.6rem)] text-[var(--ink)]" data-testid="hero-title">
          <MaskLine key={`a-${t.hero.l1}`} delay={0.25}>{t.hero.l1}</MaskLine>
          <MaskLine key={`b-${t.hero.em}`} delay={0.4}><span className="font-serif-i font-normal tracking-[-0.02em] text-[var(--red)]">{t.hero.em}</span>{t.hero.l2}</MaskLine>
          <MaskLine key={`c-${t.hero.l3}`} delay={0.55}>{t.hero.l3}</MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-md text-base md:text-lg text-[var(--ink-2)] leading-relaxed">
          {t.hero.sub}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-10 flex flex-wrap gap-3">
          <button onClick={() => scrollToId("auto")} data-testid="hero-explore-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-[var(--red)] text-white pl-7 pr-2 py-2 text-sm font-semibold hover:bg-[var(--ink)] transition-colors">
            {t.hero.cta}
            <span className="grid place-items-center w-10 h-10 rounded-full bg-white/15 group-hover:rotate-[-45deg] transition-transform duration-500"><ArrowDownRight className="w-5 h-5" /></span>
          </button>
          <button onClick={() => scrollToId("contatti")} data-testid="hero-contact-btn"
            className="rounded-full border border-[var(--ink)] px-7 py-4 text-sm font-semibold hover:bg-[var(--ink)] hover:text-white transition-colors">
            {t.hero.cta2}
          </button>
        </motion.div>
      </div>
      <div className="lg:col-span-6"><HeroImage /></div>
    </div>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14 md:mt-20 grid sm:grid-cols-3 border-t border-[var(--line)]">
      {t.hero.stats.map(([title, d], i) => { const Icon = ICONS[i]; return (
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 + i * 0.12, duration: 0.8 }}
          className="flex items-center gap-4 py-6 sm:pr-6 border-b sm:border-b-0 sm:border-r last:border-0 border-[var(--line)] sm:[&:not(:first-child)]:pl-6">
          <Icon className="w-6 h-6 text-[var(--red)] shrink-0" strokeWidth={1.6} />
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{title}</p>
            <p className="font-semibold">{d}</p>
          </div>
        </motion.div>
      ); })}
    </div>
  </section>
  );
};
